<template>
  <div class="double-slider-filter" data-testid="double-slider-filter">
    <div class="double-slider-filter__titles">
      <Heading class="double-slider-filter__title-left" :level="6">
        {{ leftTitle }}
      </Heading>
      <Heading class="double-slider-filter__title-right" :level="6">
        {{ rightTitle }}
      </Heading>
    </div>

    <div class="double-slider-filter__inputs">
      <InputNumber
        v-model.number="leftValueInternal"
        class="double-slider-filter__input"
        :min="min"
        :max="max"
        :step="step"
        :placeholder="String(min)"
        data-testid="input-number-left"
      />

      <InputNumber
        v-model.number="rightValueInternal"
        class="double-slider-filter__input double-slider-filter__input--right"
        :min="min"
        :max="max"
        :step="step"
        placeholder="+60"
        data-testid="input-number-right"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { Heading, InputNumber } from '@warrenbrasil/nebraska-web';

interface Props {
  leftTitle?: string;
  rightTitle?: string;
  leftValue?: number;
  rightValue?: number;
  min?: number;
  max?: number;
  step?: number;
}

interface Emits {
  (e: 'update:leftValue', value: number): void;
  (e: 'update:rightValue', value: number): void;
  (e: 'change', payload: { left: number; right: number }): void;
}

const props = withDefaults(defineProps<Props>(), {
  leftTitle: '',
  rightTitle: '',
  leftValue: undefined,
  rightValue: undefined,
  min: 0,
  max: 100,
  step: 1
});

const emit = defineEmits<Emits>();

const leftValueInternal = ref<number | undefined>(props.leftValue ?? undefined);
const rightValueInternal = ref<number | undefined>(
  props.rightValue ?? undefined
);

watch(
  () => props.leftValue,
  v => (leftValueInternal.value = v)
);
watch(
  () => props.rightValue,
  v => (rightValueInternal.value = v)
);

watch(leftValueInternal, val => {
  emit('update:leftValue', val as number);
  emit('change', {
    left: val as number,
    right: rightValueInternal.value as number
  });
});

watch(rightValueInternal, val => {
  emit('update:rightValue', val as number);
  emit('change', {
    left: leftValueInternal.value as number,
    right: val as number
  });
});
</script>

<style lang="less" scoped>
.double-slider-filter {
  display: flex;
  flex-direction: column;
  padding-bottom: @size-spacing-x700;

  &__titles {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: @size-spacing-x300;
  }

  &__title-left {
    margin: 0;
    color: @element-secondary;
  }

  &__title-right {
    margin: 0;
    text-align: right;
    color: @element-secondary;
  }

  &__inputs {
    display: flex;
    gap: @size-spacing-x600;
  }

  &__input {
    flex: 1 1 0;
    position: relative;
    z-index: 0;

    ::v-deep .trailing-icon {
      display: none;
    }

    /* ensure horizontal spacing inside the actual input so values don't touch the border */
    ::v-deep input,
    ::v-deep .wrn-input input,
    ::v-deep input[type='number'] {
      border-radius: @size-radius-x300;
      border: 1px solid @divider-primary !important;
      padding: 0 @size-spacing-x500 !important;
      box-sizing: border-box !important;
      background-clip: padding-box !important;
    }

    &--right {
      ::v-deep input {
        text-align: right;
      }
    }
  }
}
</style>
